import type React from "react";

import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Badge } from "@/design-system/Badge";
import { Button, buttonVariants } from "@/design-system/Button";
import { Drawer } from "@/design-system/Drawer";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

import { isProductUpdateNew, isProductUpdateUnread, type ProductUpdate } from "./productUpdates";

export type ProductUpdatesDrawerProps = {
  isOpened: boolean;
  onOpenChange: (isOpened: boolean) => void;
  updates: ProductUpdate[];
  selectedUpdateId: string;
  readUpdateIds: ReadonlySet<string>;
  onSelectUpdate: (updateId: string) => void;
  onClickAction: (update: ProductUpdate) => void;
};

export function ProductUpdatesDrawer({
  isOpened,
  onOpenChange,
  updates,
  selectedUpdateId,
  readUpdateIds,
  onSelectUpdate,
  onClickAction,
}: ProductUpdatesDrawerProps): React.ReactNode {
  const { t } = useTranslation();

  const selectedUpdate = updates.find((update) => update.id === selectedUpdateId) ?? updates[0];
  const otherUpdates = selectedUpdate ? updates.filter((update) => update.id !== selectedUpdate.id) : [];

  return (
    <Drawer.Root
      title={t("component.product-updates.title")}
      closeLabel={t("common.action.close")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      data-testid="product-updates-drawer"
      closeButtonTestId="product-updates-close-button"
    >
      <Drawer.Body>
        {selectedUpdate && (
          <>
            <article aria-labelledby="product-update-title">
              <img
                src={selectedUpdate.imageSrc}
                alt={t(selectedUpdate.imageAltKey)}
                className="aspect-5/3 w-full rounded-lg object-cover"
              />

              <header className="mt-space-md flex flex-col gap-space-sm">
                {isProductUpdateNew(selectedUpdate.publishedAt) && (
                  <Badge variant="special">{t("common.label.new")}</Badge>
                )}
                <h4 id="product-update-title" className="text-heading-4 text-text-primary">
                  {t(selectedUpdate.titleKey)}
                </h4>
                <p className="text-body text-text-secondary">{t(selectedUpdate.descriptionKey)}</p>
              </header>

              <p className="mt-space-sm text-caption text-text-tertiary">
                <FormattedDate format="date" date={selectedUpdate.publishedAt} />
              </p>

              {selectedUpdate.action && (
                <footer className="mt-space-md">
                  <Button variant="filled" onClick={() => onClickAction(selectedUpdate)}>
                    {t(selectedUpdate.action.labelKey)}
                  </Button>
                </footer>
              )}
            </article>

            {otherUpdates.length > 0 && (
              <section
                aria-labelledby="product-update-more"
                className="mt-space-lg border-t border-border-secondary pt-space-lg"
              >
                <h4 id="product-update-more" className="text-caption-emphasis text-text-tertiary">
                  {t("component.product-updates.more-updates")}
                </h4>

                <ul className="mt-space-sm flex flex-col divide-y divide-border-secondary">
                  {otherUpdates.map((update) => {
                    const isUnread = isProductUpdateUnread(update, readUpdateIds);

                    return (
                      <li key={update.id}>
                        <button
                          type="button"
                          onClick={() => onSelectUpdate(update.id)}
                          className={twResolve(
                            buttonVariants({ variant: "ghostSecondary", type: "button", fullWidth: true }),
                            "w-full overflow-visible text-left text-clip whitespace-normal hover:bg-background-default-hover focus-visible:ring-1 focus-visible:ring-border-brand focus-visible:ring-offset-0 focus-visible:ring-inset",
                          )}
                        >
                          <span className="flex w-full items-center gap-space-sm p-space-sm">
                            <span className="flex flex-1 flex-col gap-space-xs">
                              <span className="text-body-emphasis text-text-primary">{t(update.titleKey)}</span>
                              <span className="text-caption text-text-tertiary">
                                <FormattedDate format="date" date={update.publishedAt} />
                              </span>
                            </span>
                            {isUnread && (
                              <>
                                <ProductUpdateUnreadDot />
                                <span className="sr-only">{t("component.product-updates.unread")}</span>
                              </>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        )}
      </Drawer.Body>
    </Drawer.Root>
  );
}

export function ProductUpdateUnreadDot(): React.ReactNode {
  return (
    <span
      aria-hidden
      className="inline-block size-space-sm shrink-0 rounded-full bg-background-brand ring-2 ring-background-default"
    />
  );
}
